import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import ServiceFormDialog from "@/components/ServiceFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useCurrency } from "@/lib/currency";
import { Clock3, Pencil, Plus, Search, Scissors, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function Services() {
  const { fmtMoney } = useCurrency();
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = () => Promise.all([api.get("/services"), api.get("/staff")])
    .then(([serviceResponse, staffResponse]) => {
      setServices(serviceResponse.data || []);
      setStaff(staffResponse.data || []);
    });
  useEffect(() => { load().catch(() => {}); }, []);

  const visible = useMemo(() => services.filter((service) => {
    if (!showInactive && service.active === false) return false;
    const q = query.trim().toLowerCase();
    return !q || [service.name, service.code, service.category].some((value) => value?.toLowerCase().includes(q));
  }), [services, query, showInactive]);

  const staffNames = (ids = []) => ids.map((id) => staff.find((member) => member.id === id)?.name).filter(Boolean);
  const remove = async () => {
    try {
      await api.delete(`/services/${removeTarget.id}`);
      toast.success("Service removed");
      setRemoveTarget(null);
      load();
    } catch {
      toast.error("Failed to remove service");
    }
  };

  return (
    <div data-testid="services-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Services"
        subtitle={`${services.length} offerings · prices, duration and assigned team`}
        action={<Button data-testid="add-service-btn" onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-black text-white hover:bg-black/85"><Plus className="w-4 h-4 mr-1.5" /> Add service</Button>}
      />
      <Card>
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
            <Input data-testid="services-search" placeholder="Search services or categories..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 rounded-xl border-black/10 bg-white" />
          </div>
          <button type="button" onClick={() => setShowInactive((value) => !value)} className={`rounded-xl px-3.5 text-[12.5px] border transition-colors ${showInactive ? "bg-[#F8EEF4] border-[#E879A8]/40 text-[#9D3D6C]" : "bg-white border-black/10 text-[#6B6B73]"}`}>
            {showInactive ? "Showing inactive" : "Active services"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((service) => {
            const names = staffNames(service.staff_ids);
            return (
              <div key={service.id} data-testid={`service-${service.code}`} className={`relative p-5 rounded-2xl border card-hover ${service.active === false ? "bg-[#F5F5F7] border-black/[0.05] opacity-70" : "bg-white border-black/[0.05]"}`}>
                <div className="absolute left-0 top-5 bottom-5 w-1 rounded-r-full" style={{ background: service.color || "#E879A8" }} />
                <div className="flex items-start justify-between gap-3 pl-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#8E8E93] font-semibold">
                      <span>{service.category || "Other"}</span>
                      {service.active === false && <span className="text-[#FF3B30]">Inactive</span>}
                    </div>
                    <div className="mt-1 font-display font-semibold text-[16px] truncate">{service.name}</div>
                    <div className="mt-1 text-[11px] text-[#8E8E93] font-mono">{service.code}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`service-edit-${service.code}`} onClick={() => { setEditing(service); setOpen(true); }} className="p-1.5 text-[#8E8E93] hover:text-[#007AFF]"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`service-delete-${service.code}`} onClick={() => setRemoveTarget(service)} className="p-1.5 text-[#8E8E93] hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between pl-2">
                  <div className="flex items-center gap-2 text-[12px] text-[#6B6B73]"><Clock3 className="w-3.5 h-3.5 text-[#E879A8]" /> {service.duration_minutes} min</div>
                  <div className="font-mono font-semibold text-[17px]">{fmtMoney(service.price)}</div>
                </div>
                {service.description && <p className="mt-3 pl-2 text-[12px] leading-relaxed text-[#6B6B73] line-clamp-2">{service.description}</p>}
                <div className="mt-4 pt-3 pl-2 border-t border-black/[0.05] flex items-center gap-1.5 text-[11px] text-[#8E8E93]">
                  {names.length > 0 ? <><Users className="w-3.5 h-3.5" /> {names.join(", ")}</> : <><Scissors className="w-3.5 h-3.5" /> No team assignment</>}
                </div>
              </div>
            );
          })}
          {visible.length === 0 && <div className="col-span-full py-10 text-center text-[13px] text-[#8E8E93]">No services match your search.</div>}
        </div>
      </Card>
      <ServiceFormDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
      <AlertDialog open={!!removeTarget} onOpenChange={(next) => !next && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove service?</AlertDialogTitle>
            <AlertDialogDescription>{removeTarget?.name} will be permanently removed from the service menu. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white" onClick={remove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}